import { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "@/components/common/customButton";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useDataCompleteness } from "@/context/DataCompletenessContext";
import { useReferenceLists } from "@/context/GlobalGetLists";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import { saveKillReason } from "@/services/dataCompletenessApi";
import type { ApiResponse } from "@/services/genesisApi/httpsMethods";
import type { KillReason } from "@/types/deal";
import type { MissingKillReasonItem } from "@/types/dataCompleteness";
import { DataStatusDot } from "./DataCompletenessButton";

function KillReasonRow({
  item,
  onSaved,
  onError,
  onOpenDeal,
}: {
  item: MissingKillReasonItem;
  onSaved: () => void;
  onError: (response: ApiResponse) => void;
  onOpenDeal: () => void;
}) {
  const { killReasons } = useReferenceLists();
  const [reasonId, setReasonId] = useState<number | "">("");
  const [saving, setSaving] = useState(false);
  const reason = killReasons.find((r: KillReason) => r.id === reasonId);

  const save = async () => {
    if (!reason) return;
    setSaving(true);
    const response = await saveKillReason(item.deal_id, reason);
    setSaving(false);
    if (response.success) onSaved();
    else onError(response);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75 }}>
      <Link
        component="button"
        variant="body2"
        onClick={onOpenDeal}
        sx={{ flex: 1, textAlign: "left", minWidth: 0 }}
      >
        {item.deal_name}
      </Link>
      <TextField
        select
        size="small"
        label="Kill reason"
        value={reasonId}
        onChange={(e) => setReasonId(Number(e.target.value))}
        sx={{ width: 220 }}
      >
        {killReasons.map((r: KillReason) => (
          <MenuItem key={r.id} value={r.id}>
            {r.kill_reason}
          </MenuItem>
        ))}
      </TextField>
      <CustomButton
        handleButtonPress={() => void save()}
        isButtonDisabled={!reason || saving}
        isLoading={saving}
        isLoadingText="Saving"
        text="Save"
        solid
        widthOverride="80px"
      />
    </Box>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

/** What the current user still has to fill in, with kill reasons fixable in place. */
export function DataCompletenessDialog() {
  const navigate = useNavigate();
  const { data, outstanding, refresh, dialogOpen, closeDialog } =
    useDataCompleteness();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const openDeal = (dealId: number, tab?: string) => {
    closeDialog();
    navigate(tab ? `/deals/${dealId}?tab=${tab}` : `/deals/${dealId}`);
  };

  const kills = data?.items.missing_kill_reason ?? [];
  const fees = data?.items.missing_advisor_fees ?? [];

  return (
    <>
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DataStatusDot red={outstanding > 0} />
          Your deal data
        </DialogTitle>
        <DialogContent>
          {outstanding === 0 ? (
            <Typography variant="body2">
              All up to date. Nothing on the deals you lead needs your input.
            </Typography>
          ) : (
            <Typography variant="body2">
              These deals you lead are missing data. Your light turns green
              once they are filled in.
            </Typography>
          )}

          {kills.length > 0 && (
            <>
              <SectionTitle>Killed deals without a kill reason</SectionTitle>
              {kills.map((item) => (
                <KillReasonRow
                  key={item.deal_id}
                  item={item}
                  onSaved={() => void refresh()}
                  onError={showErrorFromResponse}
                  onOpenDeal={() => openDeal(item.deal_id)}
                />
              ))}
            </>
          )}

          {fees.length > 0 && (
            <>
              <SectionTitle>Deals in closing missing advisor fee quotes</SectionTitle>
              {fees.map((item) => (
                <Box
                  key={`${item.deal_id}-${item.accordion_name ?? ""}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75 }}
                >
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => openDeal(item.deal_id, "advisorFees")}
                    sx={{ flex: 1, textAlign: "left", minWidth: 0 }}
                  >
                    {item.deal_name}
                    {item.accordion_name ? ` (Add-on: ${item.accordion_name})` : ""}
                  </Link>
                  <Typography variant="body2" color="text.secondary">
                    Missing {item.missing.join(", ")}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            handleButtonPress={closeDialog}
            isButtonDisabled={false}
            text="Close"
            solid={false}
          />
        </DialogActions>
      </Dialog>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
